import { useMemo, useState } from 'react';
import {
  Box,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Chip,
  Typography,
  Divider,
  Menu,
  MenuItem,
  ListSubheader,
} from '@mui/material';
import type { FHRequest } from '../types';
import { DECLINE_REASONS } from '../api/request_api';
import { stripHtml } from './SafeHtml';
import { StatusChip } from './StatusChip';
import { StarRating } from './StarRating';

export type RequestListItemVariant = 'available' | 'claimed' | 'mentor' | 'manager';

interface RequestListItemProps {
  request: FHRequest;
  showDivider?: boolean;
  variant: RequestListItemVariant;
  onClaim?: (requestKey: string) => void;
  onHide?: (requestKey: string, reason: number) => void;
  onClick?: (requestKey: string) => void;
  clickBehavior?: 'navigate' | 'expand';
  currentUserUuid?: string;
}

export const RequestListItem = ({
  request,
  showDivider = false,
  variant,
  onClaim,
  onHide,
  onClick,
  clickBehavior = 'navigate',
  currentUserUuid,
}: RequestListItemProps) => {
  const isAvailable = variant === 'available';
  const isClaimed = variant === 'claimed';
  const isMentor = variant === 'mentor';
  const isManager = variant === 'manager';

  const [isExpanded, setIsExpanded] = useState(false);
  const [hideMenuAnchor, setHideMenuAnchor] = useState<HTMLElement | null>(null);

  // Strip HTML and show truncated or full content based on expanded state
  const displayContent = useMemo(() => {
    const content = request.content_type === 'text/html'
      ? stripHtml(request.content)
      : request.content;

    if (isExpanded) {
      return content;
    }
    return content.substring(0, 100) + (content.length > 100 ? '...' : '');
  }, [request.content, request.content_type, isExpanded]);

  return (
    <Box>
      {showDivider && <Divider />}
      <ListItem
        component="div"
        onClick={() => {
          if (clickBehavior === 'expand') {
            setIsExpanded(!isExpanded);
          } else if (onClick) {
            onClick(request.key);
          }
        }}
        sx={{
          cursor: 'pointer',
          borderRadius: 1,
          transition: 'background-color 0.15s ease',
          '&:hover': {
            bgcolor: 'action.hover'
          },
        }}
      >
        <ListItemText
          primary={request.title || 'Untitled Request'}
          sx={{ pr: 12 }}
          secondary={
            <>
              {displayContent}
              {/* Display internal manager note (visible to all assistant views) */}
              {request.god_note && (
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    mt: 1,
                    fontStyle: 'italic',
                    color: 'warning.main',
                  }}
                >
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ fontStyle: 'italic' }}
                  >
                    (Internal note from manager)
                  </Typography>{' '}
                  "{request.god_note}"
                </Box>
              )}
              <br />
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
              >
                {new Date(request.date_created).toLocaleString()}
                {request.user_name && ` · ${request.user_name}`}
                {request.assistant_name && ` · Assistant: ${request.assistant_name}`}
              </Typography>
              {request.star_rating && (
                <Box component="span" sx={{ display: 'inline-flex', ml: 1, verticalAlign: 'middle' }}>
                  <StarRating value={request.star_rating} compact readOnly />
                </Box>
              )}
              {isExpanded && clickBehavior === 'expand' && onClick && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClick(request.key);
                    }}
                  >
                    View Request
                  </Button>
                </Box>
              )}
            </>
          }
        />
        <ListItemSecondaryAction>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            {isAvailable && onClaim && (
              <Button
                variant="contained"
                size="small"
                onClick={() => onClaim(request.key)}
              >
                Claim
              </Button>
            )}
            {isAvailable && onHide && (
              <>
                <Button
                  size="small"
                  color="inherit"
                  sx={{ color: 'text.secondary', minWidth: 0, py: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setHideMenuAnchor(e.currentTarget);
                  }}
                >
                  Hide
                </Button>
                {/* Menu clicks bubble (through the portal) to the row's onClick; stop them */}
                <Menu
                  anchorEl={hideMenuAnchor}
                  open={Boolean(hideMenuAnchor)}
                  onClose={() => setHideMenuAnchor(null)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ListSubheader sx={{ lineHeight: 2.5 }}>Hide because…</ListSubheader>
                  {DECLINE_REASONS.map((reason) => (
                    <MenuItem
                      key={reason.value}
                      dense
                      onClick={() => {
                        setHideMenuAnchor(null);
                        onHide(request.key, reason.value);
                      }}
                    >
                      {reason.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
            {!isMentor && !isManager && (
              <Chip
                label={`$${(request.price || 0).toFixed(2)}`}
                size="small"
                variant="outlined"
                color="success"
              />
            )}
            {isClaimed && (
              <StatusChip status={request.status} />
            )}
            {isMentor && (
              <Chip
                label={
                  request.mentor_claimed
                    ? (request.mentor_uuid === currentUserUuid ? "You're reviewing" : 'Claimed')
                    : 'Awaiting Review'
                }
                size="small"
                color={request.mentor_claimed ? (request.mentor_uuid === currentUserUuid ? 'success' : 'info') : 'warning'}
              />
            )}
            {isManager && (
              <Chip
                label="Needs Attention"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
        </ListItemSecondaryAction>
      </ListItem>
    </Box>
  );
};
